/**
 * Brand Apply — /marketplace/sell/brand-apply
 *
 * Entry point: Sell FAB → "Apply as a Brand" link below individual seller option.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Upload, CheckCircle2, Clock, XCircle, FileText } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";
import OwnerAccountStep from "../../components/common/OwnerAccountStep";

const CATEGORIES = [
  { id: "equipment", label: "Equipment" },
  { id: "supplements", label: "Supplements (food)" },
  { id: "diet_food", label: "Diet Food" },
  { id: "merchandise", label: "Merchandise" },
  { id: "clothing", label: "Clothing" },
];

const FOOD_CATEGORIES = new Set(["supplements", "diet_food"]);

export default function BrandApply() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [existing, setExisting] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState({
    brand_name: "",
    gstin: "",
    business_category: "equipment",
    fssai_number: "",
    fssai_doc_b64: null,
    gst_doc_b64: null,
    contact_email: user?.email || "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Guests skip the /marketplace/brand/me lookup — they don't have an
    // account yet, and any 401 would set `loaded` prematurely without a
    // meaningful `existing` payload anyway.
    if (!user) {
      setLoaded(true);
      return;
    }
    apiClient
      .get("/marketplace/brand/me")
      .then((r) => {
        setExisting(r.data);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [user]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const onFile = (key, file, maxMB = 10) => {
    if (!file) return;
    if (file.size > maxMB * 1024 * 1024) {
      return toast.error(`File exceeds ${maxMB}MB`);
    }
    if (!["image/jpeg", "image/png", "application/pdf"].includes(file.type)) {
      return toast.error("JPG, PNG, or PDF only");
    }
    const reader = new FileReader();
    reader.onload = () => set(key, reader.result);
    reader.readAsDataURL(file);
  };

  const needsFssai = FOOD_CATEGORIES.has(form.business_category);

  const submit = async () => {
    if (!form.brand_name.trim()) return toast.error("Brand name required");
    if (form.gstin.trim().length !== 15) return toast.error("GSTIN must be 15 characters");
    if (!form.gst_doc_b64) return toast.error("Upload GST certificate");
    if (needsFssai && (!form.fssai_number || !form.fssai_doc_b64)) {
      return toast.error("FSSAI number + document required for food categories");
    }
    if (!form.contact_email.trim().includes("@")) return toast.error("Valid email required");

    setBusy(true);
    try {
      await apiClient.post("/marketplace/brand/apply", {
        brand_name: form.brand_name.trim(),
        gstin: form.gstin.trim().toUpperCase(),
        business_category: form.business_category,
        fssai_number: needsFssai ? form.fssai_number.trim() : null,
        fssai_doc_b64: needsFssai ? form.fssai_doc_b64 : null,
        gst_doc_b64: form.gst_doc_b64,
        contact_email: form.contact_email.trim(),
      });
      toast.success("Application submitted");
      // Refresh to show status screen
      const { data } = await apiClient.get("/marketplace/brand/me");
      setExisting(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) {
    return (
      <div className="app-page min-h-screen grid place-items-center">
        <div className="text-ink-soft">Loading…</div>
      </div>
    );
  }

  // Guest gate — inline signup. On success the user object populates the
  // store, `useAuthStore` re-renders, and the rest of the form falls through.
  if (!user) {
    return (
      <div className="app-page min-h-screen pb-28" data-testid="brand-apply-signup" style={{ background: "#F4F3F1" }}>
        <Header onBack={() => navigate(-1)} title="Apply as a Brand" />
        <main className="max-w-2xl mx-auto px-4 pt-4 pb-8">
          <OwnerAccountStep
            theme="brand"
            stepLabel="Step 1 — Brand owner account"
            subtitle="One quick account so we can verify your GST/FSSAI and route enquiries. Sign in later at fitfrik.com/login to reach your seller dashboard."
            emailDefault={form.contact_email}
            onDone={(u) => {
              set("contact_email", u?.email || form.contact_email);
              toast.success("Account created — continue your brand application");
            }}
          />
        </main>
      </div>
    );
  }

  // Status screens (pending / approved / rejected)
  if (existing?.application) {
    const app = existing.application;
    return (
      <div
        className="app-page min-h-screen"
        data-testid="brand-apply-status"
        style={{ background: "#F4F3F1" }}
      >
        <Header onBack={() => navigate(-1)} title="Brand application" />
        <main className="max-w-2xl mx-auto px-4 pt-6 pb-24">
          <StatusCard status={app.status} app={app} onReapply={() => setExisting({ application: null, is_brand: false })} />
        </main>
      </div>
    );
  }

  return (
    <div className="app-page min-h-screen pb-28" data-testid="brand-apply" style={{ background: "#F4F3F1" }}>
      <Header onBack={() => navigate(-1)} title="Apply as a Brand" />

      <main className="max-w-2xl mx-auto px-4 pt-4 space-y-3">
        <p className="text-[13px] text-ink-soft">
          Brand sellers can list supplements and clothing, get higher placement in search,
          and carry a verified badge on every listing.
        </p>

        <Field label="Brand name">
          <input
            value={form.brand_name}
            onChange={(e) => set("brand_name", e.target.value)}
            placeholder="e.g. FitCore Nutrition"
            data-testid="brand-name"
            className="w-full h-12 px-3 rounded-[12px] border border-line bg-white outline-none"
          />
        </Field>

        <Field label="GSTIN (15 characters)">
          <input
            value={form.gstin}
            onChange={(e) => set("gstin", e.target.value.toUpperCase().slice(0, 15))}
            placeholder="27AABCT1234F1Z5"
            data-testid="brand-gstin"
            className="w-full h-12 px-3 rounded-[12px] border border-line bg-white outline-none font-mono text-[13px]"
          />
        </Field>

        <Field label="Business category">
          <div className="grid grid-cols-2 gap-2" data-testid="brand-category">
            {CATEGORIES.map((c) => {
              const on = form.business_category === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => set("business_category", c.id)}
                  data-testid={`cat-${c.id}`}
                  className="h-11 px-3 rounded-pill text-[13px] font-semibold"
                  style={{
                    background: on ? "#00D4FF" : "#ECEAE7",
                    color: on ? "#0B0B0B" : "#6B6760",
                    borderRadius: 100,
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </Field>

        {needsFssai && (
          <>
            <Field label="FSSAI licence number">
              <input
                value={form.fssai_number}
                onChange={(e) => set("fssai_number", e.target.value)}
                placeholder="14-digit FSSAI number"
                data-testid="brand-fssai-number"
                className="w-full h-12 px-3 rounded-[12px] border border-line bg-white outline-none"
              />
            </Field>
            <UploadField
              label="FSSAI document (JPG/PDF, max 10MB)"
              file={form.fssai_doc_b64}
              onFile={(f) => onFile("fssai_doc_b64", f)}
              onClear={() => set("fssai_doc_b64", null)}
              testid="brand-fssai-doc"
            />
          </>
        )}

        <UploadField
          label="GST certificate (JPG/PDF, max 10MB)"
          file={form.gst_doc_b64}
          onFile={(f) => onFile("gst_doc_b64", f)}
          onClear={() => set("gst_doc_b64", null)}
          testid="brand-gst-doc"
        />

        <Field label="Contact email for verification updates">
          <input
            value={form.contact_email}
            onChange={(e) => set("contact_email", e.target.value)}
            placeholder="you@brand.com"
            data-testid="brand-email"
            className="w-full h-12 px-3 rounded-[12px] border border-line bg-white outline-none"
          />
        </Field>

        <button
          onClick={submit}
          disabled={busy}
          data-testid="brand-submit"
          className="w-full mt-4 h-12 rounded-pill disabled:opacity-50"
          style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
        >
          {busy ? "Submitting…" : "Submit Application"}
        </button>
        <p className="text-[11px] text-ink-soft text-center">
          Our team verifies brand applications within 2–3 business days.
        </p>
      </main>
    </div>
  );
}

function Header({ onBack, title }) {
  return (
    <header className="sticky top-0 z-20 bg-canvas/90 backdrop-blur-xl border-b border-line2">
      <div className="max-w-2xl mx-auto h-14 px-3 flex items-center gap-2">
        <button
          onClick={onBack}
          aria-label="Back"
          data-testid="brand-apply-back"
          className="w-10 h-10 grid place-items-center"
        >
          <ChevronLeft size={22} />
        </button>
        <h1 className="font-barlow-cond font-extrabold text-[22px]">{title}</h1>
      </div>
    </header>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.1em] font-bold text-ink-tertiary mb-1.5">
        {label}
      </div>
      {children}
    </div>
  );
}

function UploadField({ label, file, onFile, onClear, testid }) {
  return (
    <Field label={label}>
      {!file ? (
        <label
          className="flex items-center justify-center gap-2 h-14 rounded-[12px] border-2 border-dashed cursor-pointer bg-white"
          style={{ borderColor: "#E2E0DC" }}
          data-testid={testid}
        >
          <Upload size={16} className="text-ink-tertiary" />
          <span className="text-[13px] font-semibold text-ink-soft">Tap to upload</span>
          <input
            type="file"
            accept="image/jpeg,image/png,application/pdf"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
        </label>
      ) : (
        <div
          className="flex items-center gap-2 h-14 px-3 rounded-[12px] bg-white border border-line2"
          data-testid={`${testid}-uploaded`}
        >
          <FileText size={18} style={{ color: "#00B0D5" }} />
          <span className="flex-1 text-[13px] font-semibold">Uploaded</span>
          <button
            onClick={onClear}
            className="text-[12px] font-semibold text-ink-soft"
          >
            Change
          </button>
        </div>
      )}
    </Field>
  );
}

function StatusCard({ status, app, onReapply }) {
  if (status === "pending") {
    return (
      <div
        className="bg-white border border-line2 rounded-[16px] p-6 text-center"
        data-testid="brand-status-pending"
      >
        <div
          className="w-14 h-14 mx-auto rounded-full grid place-items-center"
          style={{ background: "rgba(192,132,0,0.14)", color: "#C08400" }}
        >
          <Clock size={22} />
        </div>
        <h2 className="font-barlow-cond font-extrabold text-[22px] mt-3">Under Review</h2>
        <p className="text-[13px] text-ink-soft mt-2">
          Our team verifies brand applications within 2–3 business days. We&apos;ll email{" "}
          <span className="font-semibold">{app.contact_email}</span> as soon as a decision is made.
        </p>
        <div className="mt-4 text-[12px] text-ink-tertiary">
          Submitted on {new Date(app.submitted_at).toLocaleDateString("en-IN")}
        </div>
      </div>
    );
  }
  if (status === "approved") {
    return (
      <div
        className="bg-white border border-line2 rounded-[16px] p-6 text-center"
        data-testid="brand-status-approved"
      >
        <div
          className="w-14 h-14 mx-auto rounded-full grid place-items-center"
          style={{ background: "rgba(0,160,103,0.14)", color: "#00A067" }}
        >
          <CheckCircle2 size={22} />
        </div>
        <h2 className="font-barlow-cond font-extrabold text-[22px] mt-3">Verified brand seller</h2>
        <p className="text-[13px] text-ink-soft mt-2">
          You can now list supplements, clothing, and all product categories on FitFrik Marketplace.
        </p>
      </div>
    );
  }
  return (
    <div
      className="bg-white border border-line2 rounded-[16px] p-6 text-center"
      data-testid="brand-status-rejected"
    >
      <div
        className="w-14 h-14 mx-auto rounded-full grid place-items-center"
        style={{ background: "rgba(229,72,77,0.14)", color: "#E5484D" }}
      >
        <XCircle size={22} />
      </div>
      <h2 className="font-barlow-cond font-extrabold text-[22px] mt-3">Application not approved</h2>
      {app.rejection_reason && (
        <p className="text-[13px] text-ink-soft mt-2">{app.rejection_reason}</p>
      )}
      <button
        onClick={onReapply}
        data-testid="brand-reapply"
        className="mt-4 px-5 h-11 rounded-pill font-bold"
        style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}
      >
        Fix and re-apply
      </button>
    </div>
  );
}
